import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Me } from '@zibanu/shared';
import { useDeps } from './deps';

export type Membership = Me['memberships'][number];

/** Who this session is — null when there is none. Everything the app shows follows from it. */
export function useMe() {
  const { client } = useDeps();
  return useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<Me | null> => {
      const res = await client.me();
      if (res.ok) return res.data;
      if (res.error === 'unauthenticated') return null;
      throw new Error(res.error);
    },
    staleTime: 60_000,
  });
}

interface Books {
  /** The books on screen; null until known. */
  current: Membership | null;
  choose(orgId: string): void;
}

const BooksContext = createContext<Books>({ current: null, choose: () => undefined });

/**
 * Which books are open: the ones this device chose last, else the ones the
 * person chats with, else the first (spec 3A §6.2). Independent of chat's
 * active books (parent spec §3.4).
 */
export function BooksProvider({ children }: { children: ReactNode }) {
  const { client } = useDeps();
  const me = useMe().data;
  const personId = me?.person.personId;
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  // Read again when the person changes: a shared phone's wipe clears the saved choice.
  useEffect(() => {
    let live = true;
    void client.preferredOrg.get().then((orgId) => {
      if (live) setChosen(orgId);
    });
    return () => {
      live = false;
    };
  }, [client, personId]);
  const choose = useCallback(
    (orgId: string) => {
      setChosen(orgId);
      void client.preferredOrg.set(orgId);
    },
    [client],
  );
  const current = useMemo(() => {
    if (chosen === undefined || !me) return null;
    const all = me.memberships;
    return all.find((m) => m.orgId === chosen) ?? all.find((m) => m.orgId === me.chatOrgId) ?? all[0] ?? null;
  }, [chosen, me]);
  return <BooksContext.Provider value={{ current, choose }}>{children}</BooksContext.Provider>;
}

export function useBooks(): Books {
  return useContext(BooksContext);
}

/** May the person do this here? Only what /me offers (spec 3A W12) — the server still decides each call. */
export function useOffers(): (action: string) => boolean {
  const { current } = useBooks();
  return useCallback((action: string) => current?.actions.includes(action) ?? false, [current]);
}
